import React, {
  memo,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ReactSVG } from 'react-svg';
import { PdfViewerProps } from './types';
import ReactDOM from 'react-dom';
import { classNames } from 'helpers';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

/* ---------------- ICON BUTTON ---------------- */
const IconButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { icon: string }
> = ({ icon, className, ...props }) => (
  <button
    {...props}
    className={`p-2 hover:bg-white/10 rounded disabled:opacity-40 ${className ?? ''}`}
  >
    <ReactSVG src={icon} className="w-5 h-5 [&_svg]:stroke-white [&_svg]:fill-none" />
  </button>
);

/* ---------------- HOOK ---------------- */
function useContainerWidth<T extends HTMLElement>() {
  const [node, setNode] = useState<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!node) return;

    const observer = new ResizeObserver(entries => {
      setWidth(entries[0].contentRect.width);
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return { ref: setNode, width };
}

/* ---------------- COMPONENT ---------------- */
const PdfViewer: React.FC<PdfViewerProps> = (props: PdfViewerProps) => {
  const {
    url,
    show,
    side = "right",
    onRefresh,
  } = props;
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [isDocLoaded, setIsDocLoaded] = useState(false);

  const { ref, width } = useContainerWidth<HTMLDivElement>();

  const file = useMemo(() => url, [url]);

  useEffect(() => {
    setIsDocLoaded(false);
  }, [url]);
  const pageWidth = width > 0 ? width - 48 : undefined;

  /* ---------------- SYNC INPUT ---------------- */
  useEffect(() => {
    setPageInput(String(pageNumber));
  }, [pageNumber]);

  /* ---------------- ZOOM ---------------- */
  const ZOOM_STEP = 0.1;
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 3;

  const zoomIn = () =>
    setScale(s => Math.min(MAX_ZOOM, +(s + ZOOM_STEP).toFixed(2)));

  const zoomOut = () =>
    setScale(s => Math.max(MIN_ZOOM, +(s - ZOOM_STEP).toFixed(2)));

  const resetZoom = () => setScale(1);

  /* ---------------- PAGINATION ---------------- */
  const goToPage = (page: number) => {
    if (!numPages) return;
    const safe = Math.min(Math.max(page, 1), numPages);
    setPageNumber(safe);
  };

  const submitPageInput = () => {
    const p = Number(pageInput);
    if (!isNaN(p)) goToPage(p);
  };

  const sideTranslateClass = () => {
    if (show) return "translate-x-0 translate-y-0";

    switch (side) {
      case "left":
        return "-translate-x-full";
      case "right":
        return "translate-x-full";
      case "top":
        return "-translate-y-full";
      case "bottom":
        return "translate-y-full";
      default:
        return "translate-x-full";
    }
  };

  const renderPdfContent = () => {
    if (!show) return null;

    return (
      <div
        className={classNames(
          "w-full h-full bg-white transition-transform duration-500 ease-in-out",
          sideTranslateClass()
        )}
      >
        <div className="h-screen overflow-y-auto">
          {pdfContent()}
        </div>
      </div>
    );
  };

  const pdfContent = () => {
    return (
      <div className="w-full h-full bg-[#525659] relative">
        {/* TOOLBAR */}
        <div className="flex items-center justify-between h-16 px-4 bg-[#323639] text-white">
          {/* LEFT – ZOOM */}
          <div className="flex items-center gap-2 w-1/3">
            <IconButton
              icon="/media/icons/minus.svg"
              onClick={zoomOut}
              disabled={scale <= MIN_ZOOM}
            />
            <span className="text-sm w-14 text-center">
              {Math.round(scale * 100)}%
            </span>
            <IconButton
              icon="/media/icons/plus.svg"
              onClick={zoomIn}
              disabled={scale >= MAX_ZOOM}
            />
            <button onClick={resetZoom} className="text-sm underline ml-2">
              Reset
            </button>
          </div>

          {/* CENTER – PAGINATION */}
          <div className="flex items-center justify-center gap-2 w-1/3">
            {numPages && (
              <>
                <IconButton
                  icon="/media/icons/arrow-left-double.svg"
                  disabled={pageNumber === 1}
                  onClick={() => goToPage(1)}
                />

                <IconButton
                  icon="/media/icons/arrow-left.svg"
                  disabled={pageNumber === 1}
                  onClick={() => goToPage(pageNumber - 1)}
                />

                <input
                  value={pageInput}
                  onChange={e =>
                    setPageInput(e.target.value.replace(/\D/g, ''))
                  }
                  onBlur={submitPageInput}
                  onKeyDown={e => e.key === 'Enter' && submitPageInput()}
                  className="w-12 text-center bg-[#1f1f1f] border border-white/20 rounded text-white text-sm"
                />

                <span className="text-sm">/ {numPages}</span>

                <IconButton
                  icon="/media/icons/arrow-right.svg"
                  disabled={pageNumber === numPages}
                  onClick={() => goToPage(pageNumber + 1)}
                />

                <IconButton
                  icon="/media/icons/arrow-right-double.svg"
                  disabled={pageNumber === numPages}
                  onClick={() => goToPage(numPages)}
                />
              </>
            )}
          </div>

          {/* RIGHT – REFRESH & DOWNLOAD */}
          <div className="flex justify-end w-1/3 gap-1">
            {onRefresh && (
              <IconButton icon="/media/icons/refresh.svg" onClick={onRefresh} />
            )}
            <a
              href={url}
              download
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconButton icon="/media/icons/download.svg" />
            </a>
          </div>
        </div>

        {/* PDF */}
        <div className={classNames(
          "absolute inset-x-0 bottom-0 top-16 overflow-auto transition-opacity duration-500",
          isDocLoaded ? "opacity-100" : "opacity-0"
        )}>
          {!isDocLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loading />
            </div>
          )}
          <div ref={ref} className="w-full px-6 py-8 flex justify-center">
            <Document
              file={file}
              onLoadSuccess={pdf => { setNumPages(pdf.numPages); setIsDocLoaded(true); }}
              onLoadError={console.error}
              loading={<></>}
            >
              <Page
                pageNumber={pageNumber}
                width={pageWidth}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </div>
        </div>
      </div>
    )
  }

  return ReactDOM.createPortal(
    <>
      <div className={classNames("pdf-viewer fixed top-0 left-0 right-0 lg:right-1/2 z-[40] flex transition-opacity duration-300 ", show ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none")}>
        {renderPdfContent()}
      </div>
    </>,
    document.body
  );
};

const Loading: React.FC = () => (
  <div className="flex items-center justify-center h-full">
    <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin" />
  </div>
);

export default memo(PdfViewer);
